<script setup>
/* global axios */
// Evento do WhatsApp (EventMessage), encaminhado pelo AstraCall como texto:
//   📅 *Evento: <nome>*            (ou "Evento (CANCELADO): <nome>")
//   <descrição>                    (opcional)
//   🕒 <início>[ até <fim>]        (opcional, "DD/MM/YYYY HH:MM")
//   📍 <local>                     (opcional)
//   https://maps.google.com/?q=... (opcional)
//   🔗 <link de participar>        (opcional)
//   _EID: <id>_                    (opcional — p/ RSVP; depende do AstraCall)
// Card no estilo WhatsApp: bloco de calendário + detalhes + 2 botões (Responder
// RSVP / Adicionar ao calendário). Clicar no corpo abre o modal de detalhes.
// Detecção/roteamento no Message.vue (marcador 📅).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMessageContext } from '../provider.js';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const { content, conversationId } = useMessageContext();
const route = useRoute();

const detailsDialog = ref(null);
const showRsvp = ref(false);
const copied = ref('');
const rsvpError = ref('');
const rsvpDone = ref('');

const WEEKDAY = ['dom.', 'seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.'];
const MONTH = [
  'jan.',
  'fev.',
  'mar.',
  'abr.',
  'mai.',
  'jun.',
  'jul.',
  'ago.',
  'set.',
  'out.',
  'nov.',
  'dez.',
];

const parseDate = str => {
  const m = str.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, d, mo, y, hh, mm] = m.map(Number);
  return { date: new Date(y, mo - 1, d, hh, mm), raw: { d, mo, y, hh, mm } };
};

const fmtDay = d =>
  `${WEEKDAY[d.getDay()]}, ${d.getDate()} de ${MONTH[d.getMonth()]}`;
const fmtTime = d =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const ev = computed(() => {
  const raw = String(content?.value || '');
  const lines = raw.split('\n');
  const grab = re => (raw.match(re) || [])[1]?.trim() || '';

  const titleFull = grab(/📅\s*\*([^*]+)\*/);
  const canceled = /CANCELAD/i.test(titleFull);
  const name =
    titleFull.replace(/^Evento\s*(\(CANCELAD[OA]\))?\s*:?\s*/i, '').trim() ||
    'Evento';

  const timeText = grab(/🕒\s*(.+)/);
  const location = grab(/📍\s*(.+)/);
  const mapsLink =
    (raw.match(/https:\/\/maps\.google\.com\/\S+/) || [])[0] || '';
  const link = grab(/🔗\s*(\S+)/);
  const eventId = grab(/_EID:\s*([^_]+)_/);

  const startIdx = lines.findIndex(l => l.includes('📅'));
  const markerIdx = lines.findIndex(
    (l, i) => i > startIdx && /🕒|📍|🔗|https:\/\/maps/.test(l)
  );
  let description = '';
  if (startIdx > -1) {
    description = lines
      .slice(startIdx + 1, markerIdx > -1 ? markerIdx : lines.length)
      .map(l => l.trim())
      .filter(Boolean)
      .join(' ');
  }

  // datas: início e fim (se houver "até")
  const parts = timeText.split(/\s+até\s+/i);
  const start = parts[0] ? parseDate(parts[0]) : null;
  const end = parts[1] ? parseDate(parts[1]) : null;

  let dateText = timeText;
  if (start) {
    const sameDay =
      end && start.date.toDateString() === end.date.toDateString();
    if (end && sameDay) {
      dateText = `${fmtDay(start.date)} · ${fmtTime(start.date)} – ${fmtTime(end.date)}`;
    } else if (end) {
      dateText = `${fmtDay(start.date)} ${fmtTime(start.date)} – ${fmtDay(end.date)} ${fmtTime(end.date)}`;
    } else {
      dateText = `${fmtDay(start.date)} · ${fmtTime(start.date)}`;
    }
  }

  // rótulo do link conforme o tipo de chamada do WhatsApp
  let linkLabel = 'Link do evento';
  if (/call\.whatsapp\.com\/voice/.test(link))
    linkLabel = 'Ligação de voz do WhatsApp';
  else if (/call\.whatsapp\.com\/video/.test(link))
    linkLabel = 'Chamada de vídeo do WhatsApp';

  return {
    name,
    canceled,
    description,
    timeText,
    dateText,
    location,
    mapsLink,
    link,
    linkLabel,
    eventId,
    start,
    end,
  };
});

// bloco de calendário (dia/mês) a partir da data de início
const cal = computed(() => {
  if (!ev.value.start) return null;
  const d = ev.value.start.date;
  return {
    day: d.getDate(),
    month: MONTH[d.getMonth()].replace('.', '').toUpperCase(),
  };
});

const icsStamp = r =>
  `${r.y}${String(r.mo).padStart(2, '0')}${String(r.d).padStart(2, '0')}T${String(r.hh).padStart(2, '0')}${String(r.mm).padStart(2, '0')}00`;

const addToCalendar = () => {
  const e = ev.value;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AstraChat//Evento//PT',
    'BEGIN:VEVENT',
    `SUMMARY:${e.name}`,
  ];
  if (e.description) lines.push(`DESCRIPTION:${e.description}`);
  if (e.location) lines.push(`LOCATION:${e.location}`);
  if (e.start) lines.push(`DTSTART:${icsStamp(e.start.raw)}`);
  if (e.end) lines.push(`DTEND:${icsStamp(e.end.raw)}`);
  else if (e.start) lines.push(`DTEND:${icsStamp(e.start.raw)}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');

  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${e.name.replace(/[^\w\s-]/g, '').trim() || 'evento'}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const copy = async (text, tag) => {
  try {
    await navigator.clipboard.writeText(text);
    copied.value = tag;
    setTimeout(() => {
      copied.value = '';
    }, 2000);
  } catch (e) {
    copied.value = '';
  }
};

const RSVP_LABELS = { going: 'Vou', maybe: 'Talvez', declined: 'Não vou' };

const sendRsvp = async response => {
  showRsvp.value = false;
  rsvpError.value = '';
  if (!ev.value.eventId) {
    rsvpError.value =
      'Resposta ao evento ainda não disponível para este evento.';
    return;
  }
  try {
    const base = `/api/v1/accounts/${route.params.accountId}/kanban/connections`;
    await axios.post(`${base}/event_response`, {
      conversation_id: conversationId?.value,
      event_message_id: ev.value.eventId,
      response,
    });
    rsvpDone.value = response;
  } catch (e) {
    rsvpError.value = e?.response?.data?.error || 'Falha ao enviar a resposta';
  }
};
</script>

<template>
  <BaseBubble
    class="!p-0 overflow-hidden !max-w-[330px] min-w-[280px]"
    data-bubble-name="event"
    hide-meta
  >
    <!-- corpo (clicável → detalhes) -->
    <button
      type="button"
      class="flex w-full gap-3 p-3.5 text-left hover:bg-n-alpha-1"
      :class="ev.canceled ? 'opacity-70' : ''"
      @click="detailsDialog?.open()"
    >
      <div
        v-if="cal"
        class="flex flex-col items-center overflow-hidden rounded-lg size-12 shrink-0 outline outline-1 outline-n-weak"
      >
        <div
          class="w-full py-0.5 text-center text-white text-[0.5rem] font-bold tracking-wide"
          :class="ev.canceled ? 'bg-n-ruby-9' : 'bg-n-brand'"
        >
          {{ cal.month }}
        </div>
        <div class="flex items-center flex-1 text-lg font-bold text-n-slate-12">
          {{ cal.day }}
        </div>
      </div>

      <div class="flex flex-col min-w-0 gap-0.5">
        <span
          v-if="ev.canceled"
          class="self-start text-[0.625rem] font-semibold uppercase px-1.5 py-0.5 rounded bg-n-ruby-3 text-n-ruby-11"
        >
          {{ t('CONVERSATION.EVENT_BUBBLE.CANCELLED') }}
        </span>
        <span
          class="text-sm font-semibold leading-tight text-n-slate-12"
          :class="ev.canceled ? 'line-through' : ''"
        >
          {{ ev.name }}
        </span>
        <span v-if="ev.dateText" class="text-xs text-n-slate-11">
          {{ ev.dateText }}
        </span>
        <span v-if="ev.location" class="text-xs truncate text-n-slate-11">
          {{ ev.location }}
        </span>
        <span v-if="ev.link" class="text-xs text-n-slate-11">
          {{ ev.linkLabel }}
        </span>
      </div>
    </button>

    <!-- botão Responder (RSVP) — as opções EXPANDEM inline (não flutuam), pra não
         serem cortadas pelo overflow-hidden da bolha -->
    <div class="border-t border-n-weak">
      <button
        type="button"
        class="flex items-center justify-center w-full gap-1 py-2.5 text-sm font-medium text-n-brand hover:bg-n-alpha-1"
        @click="showRsvp = !showRsvp"
      >
        <template v-if="rsvpDone">
          <span class="i-ph-check-bold size-4" />
          {{ RSVP_LABELS[rsvpDone] }}
        </template>
        <template v-else>
          {{ t('CONVERSATION.EVENT_BUBBLE.REPLY') }}
          <span
            class="size-3"
            :class="showRsvp ? 'i-ph-caret-up' : 'i-ph-caret-down'"
          />
        </template>
      </button>
      <div v-if="showRsvp" class="border-t border-n-weak bg-n-alpha-1">
        <button
          v-for="(label, key) in RSVP_LABELS"
          :key="key"
          type="button"
          class="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
          @click="sendRsvp(key)"
        >
          <span
            class="rounded-full size-3.5 outline outline-1 outline-n-slate-8 shrink-0"
          />
          {{ label }}
        </button>
      </div>
    </div>

    <!-- botão Adicionar ao calendário -->
    <button
      type="button"
      class="flex items-center justify-center w-full gap-2 py-2.5 text-sm font-medium border-t border-n-weak text-n-brand hover:bg-n-alpha-1"
      @click="addToCalendar"
    >
      <span class="i-ph-calendar-plus size-4" />
      {{ t('CONVERSATION.EVENT_BUBBLE.ADD_TO_CALENDAR') }}
    </button>

    <p v-if="rsvpError" class="px-3.5 py-2 text-xs text-n-ruby-11">
      {{ rsvpError }}
    </p>

    <!-- modal de detalhes -->
    <Dialog
      ref="detailsDialog"
      width="sm"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
    >
      <div class="flex items-center justify-between -mt-2">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('CONVERSATION.EVENT_BUBBLE.HEADING') }}
        </h3>
        <button
          type="button"
          class="flex text-n-slate-11 hover:text-n-slate-12"
          @click="detailsDialog?.close()"
        >
          <span class="i-ph-x-bold size-4" />
        </button>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <span
            v-if="ev.canceled"
            class="self-start text-[0.625rem] font-semibold uppercase px-1.5 py-0.5 rounded bg-n-ruby-3 text-n-ruby-11"
          >
            {{ t('CONVERSATION.EVENT_BUBBLE.CANCELLED') }}
          </span>
          <span
            class="text-lg font-semibold text-n-slate-12"
            :class="ev.canceled ? 'line-through' : ''"
          >
            {{ ev.name }}
          </span>
          <p v-if="ev.description" class="text-sm text-n-slate-11">
            {{ ev.description }}
          </p>
        </div>

        <!-- data + adicionar ao calendário -->
        <div v-if="ev.dateText" class="flex gap-3">
          <span
            class="i-ph-calendar-blank size-5 shrink-0 text-n-slate-10 mt-0.5"
          />
          <div class="flex flex-col gap-0.5">
            <span class="text-sm text-n-slate-12">{{ ev.dateText }}</span>
            <button
              type="button"
              class="self-start text-sm font-medium text-n-brand hover:underline"
              @click="addToCalendar"
            >
              {{ t('CONVERSATION.EVENT_BUBBLE.ADD_TO_CALENDAR') }}
            </button>
          </div>
        </div>

        <!-- local + copiar -->
        <div v-if="ev.location" class="flex gap-3">
          <span class="i-ph-map-pin size-5 shrink-0 text-n-slate-10 mt-0.5" />
          <div class="flex flex-col gap-0.5">
            <component
              :is="ev.mapsLink ? 'a' : 'span'"
              :href="ev.mapsLink || undefined"
              :target="ev.mapsLink ? '_blank' : undefined"
              class="text-sm text-n-slate-12"
              :class="ev.mapsLink ? 'hover:underline' : ''"
            >
              {{ ev.location }}
            </component>
            <button
              type="button"
              class="self-start text-sm font-medium text-n-brand hover:underline"
              @click="copy(ev.location, 'loc')"
            >
              {{ copied === 'loc' ? 'Copiado' : 'Copiar' }}
            </button>
          </div>
        </div>

        <!-- link da chamada + copiar link -->
        <div v-if="ev.link" class="flex gap-3">
          <span class="i-ph-phone size-5 shrink-0 text-n-slate-10 mt-0.5" />
          <div class="flex flex-col gap-0.5">
            <a
              :href="ev.link"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-n-slate-12 hover:underline"
            >
              {{ ev.linkLabel }}
            </a>
            <button
              type="button"
              class="self-start text-sm font-medium text-n-brand hover:underline"
              @click="copy(ev.link, 'link')"
            >
              {{ copied === 'link' ? 'Copiado' : 'Copiar link' }}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  </BaseBubble>
</template>
